import { useEffect, useState } from "react";
import { isChatEmbeddedInHub } from "@/suiteSession";
import { useChatPwaInstall } from "@/hooks/useChatPwaInstall";
import {
  enableChatWebPush,
  getChatWebPushStatus,
  isChatWebPushSupported,
} from "@/lib/chatWebPush";
import {
  CHAT_PWA_INSTALL_DISMISS_KEY,
  CHAT_PWA_PERMS_DISMISS_KEY,
  CHAT_PWA_PERMS_DONE_KEY,
  readLocalFlag,
  readSessionFlag,
  requestChatMediaPermissions,
  writeLocalFlag,
  writeSessionFlag,
} from "@/lib/pwa/chatPwaOnboard";

type Step = "install" | "perms" | null;

export function ChatPwaOnboard({ suiteUserId }: { suiteUserId: string }) {
  const pwa = useChatPwaInstall();
  const [step, setStep] = useState<Step>(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");
  const [showIos, setShowIos] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(() =>
    readSessionFlag(CHAT_PWA_INSTALL_DISMISS_KEY)
  );

  useEffect(() => {
    if (isChatEmbeddedInHub()) {
      setStep(null);
      return;
    }

    const permsDone = readLocalFlag(CHAT_PWA_PERMS_DONE_KEY);
    const permsDismissed = readSessionFlag(CHAT_PWA_PERMS_DISMISS_KEY);

    if (pwa.isStandalone) {
      if (permsDone || permsDismissed) {
        setStep(null);
        return;
      }
      if (!isChatWebPushSupported() || Notification.permission === "denied") {
        writeLocalFlag(CHAT_PWA_PERMS_DONE_KEY);
        setStep(null);
        return;
      }
      void getChatWebPushStatus(suiteUserId).then((status) => {
        if (status === "subscribed" || status === "unsupported" || status === "not_configured") {
          writeLocalFlag(CHAT_PWA_PERMS_DONE_KEY);
          setStep(null);
          return;
        }
        setStep("perms");
      });
      return;
    }

    if (pwa.showInstallUi && !readSessionFlag(CHAT_PWA_INSTALL_DISMISS_KEY)) {
      setStep("install");
      return;
    }
    setStep(null);
  }, [pwa.isStandalone, pwa.showInstallUi, suiteUserId]);

  if (isChatEmbeddedInHub()) return null;

  async function handleInstall() {
    if (pwa.canNativeInstall) {
      setBusy(true);
      try {
        const ok = await pwa.install();
        if (ok) setStep("perms");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (pwa.isIos) {
      setShowIos(true);
      setShowManual(false);
      return;
    }
    setShowManual(true);
    setShowIos(false);
  }

  function dismissInstall() {
    writeSessionFlag(CHAT_PWA_INSTALL_DISMISS_KEY);
    setInstallDismissed(true);
    setStep(null);
  }

  function dismissPerms() {
    writeSessionFlag(CHAT_PWA_PERMS_DISMISS_KEY);
    setStep(null);
  }

  async function enablePerms() {
    setBusy(true);
    setHint("");
    const push = await enableChatWebPush(suiteUserId);
    const media = await requestChatMediaPermissions();
    setBusy(false);
    if (push.ok || media.ok) {
      writeLocalFlag(CHAT_PWA_PERMS_DONE_KEY);
      setStep(null);
      return;
    }
    if (push.status === "denied" || media.denied) {
      setHint("Accès bloqué. Ouvrez les réglages du site pour autoriser notifications, caméra et micro.");
      return;
    }
    setHint(push.message || media.message || "Impossible d’activer les accès.");
  }

  return (
    <>
      {step === "install" ? (
        <div className="chat-pwa-onboard-backdrop" onClick={dismissInstall}>
          <div
            className="chat-pwa-onboard"
            role="dialog"
            aria-labelledby="chat-pwa-install-title"
            onClick={(e) => e.stopPropagation()}
          >
            <img src="./favicon.png" alt="" className="chat-pwa-onboard__icon" />
            <h3 id="chat-pwa-install-title">Installez Aurélia Chat</h3>
            <p>Gardez vos conversations à portée de main, avec les appels et les notifications.</p>
            <ul>
              <li>Icône sur le bureau ou l’écran d’accueil</li>
              <li>Alertes même hors de l’onglet</li>
              <li>Caméra et micro prêts pour les appels</li>
            </ul>
            <button type="button" className="chat-pwa-onboard__cta" disabled={busy} onClick={() => void handleInstall()}>
              {busy ? "Installation…" : "Télécharger l’application"}
            </button>
            {showIos ? (
              <ol className="chat-pwa-onboard__steps">
                <li>
                  Appuyez sur <strong>Partager</strong> en bas de Safari
                </li>
                <li>
                  Choisissez <strong>Sur l’écran d’accueil</strong>
                </li>
                <li>
                  Confirmez avec <strong>Ajouter</strong>
                </li>
              </ol>
            ) : null}
            {showManual ? (
              <p className="chat-pwa-onboard__hint">
                Menu du navigateur (⋮) → <strong>Installer l’application</strong> ou{" "}
                <strong>Ajouter à l’écran d’accueil</strong>.
              </p>
            ) : null}
            <button type="button" className="chat-pwa-onboard__later" onClick={dismissInstall}>
              Plus tard
            </button>
          </div>
        </div>
      ) : null}

      {step === "perms" ? (
        <div className="chat-pwa-onboard-backdrop" onClick={dismissPerms}>
          <div
            className="chat-pwa-onboard"
            role="dialog"
            aria-labelledby="chat-pwa-perms-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="chat-pwa-perms-title">Autoriser les accès</h3>
            <p>Le navigateur va demander notifications, caméra et micro. Acceptez pour les messages et les appels.</p>
            <button type="button" className="chat-pwa-onboard__cta" disabled={busy} onClick={() => void enablePerms()}>
              {busy ? "…" : "Autoriser les accès"}
            </button>
            {hint ? <p className="chat-pwa-onboard__hint">{hint}</p> : null}
            <button type="button" className="chat-pwa-onboard__later" onClick={dismissPerms}>
              Plus tard
            </button>
          </div>
        </div>
      ) : null}

      {pwa.showInstallUi && installDismissed && step !== "install" ? (
        <button type="button" className="chat-pwa-onboard-pill" onClick={() => setStep("install")}>
          Télécharger l’application
        </button>
      ) : null}
    </>
  );
}
